/**
 * L'ANGLAIS (§13) — la seconde langue de l'interface, et rien d'autre.
 *
 * LA CLEF EST LA PHRASE FRANÇAISE. C'est ce qui garde les écrans lisibles : on lit le composant, on
 * lit ce que le joueur voit. Le prix est qu'une reformulation détache sa traduction ; deux tests
 * (`tests/traduction.test.ts`) refusent une phrase sans anglais et une traduction sans emploi, ce
 * qui transforme ce silence-là en rouge.
 *
 * CE QUI N'EST PAS ICI, ET NE LE SERA JAMAIS : la prose d'une aventure, le nom d'une épée, le titre
 * d'un lieu. Ce sont des CITATIONS (§13.1) — une aventure a une langue, celle dans laquelle ses
 * neuf cents nœuds ont été écrits, et la traduire à la volée reviendrait à réécrire le livre à
 * chaque lecture.
 *
 * LE REGISTRE SE TRADUIT AUSSI. Ce jeu ne parle pas comme un logiciel : il dit « your bag will not
 * take it » et non « inventory full ». Une traduction qui perdrait le ton perdrait le jeu.
 */
export const EN: Record<string, string> = {
  /* --- Les caractéristiques (§3.1) ------------------------------------------ */
  Vie: "Life",
  VIE: "LIF",
  "+4 points de vie": "+4 hit points",
  "Ce qui vous sépare de la mort définitive. Rien d'autre.": "All that stands between you and a death that sticks. Nothing else.",
  Attaque: "Attack",
  ATQ: "ATK",
  "+1 au toucher, +1 aux dégâts, +1,5 kg portés": "+1 to hit, +1 damage, +1.5 kg carried",
  "Frapper, et faire mal. La seule qui compte deux fois : elle décide si le coup passe, puis de ce qu'il coûte à celui d'en face.":
    "To strike, and to hurt. The only one that counts twice: it decides whether the blow lands, then what it costs the one facing you.",
  "Défense": "Defence",
  DEF: "DEF",
  "+1 à la difficulté qu'on doit battre pour vous toucher": "+1 to the difficulty of hitting you",
  "Ce qui fait qu'un coup vous manque. Un point de plus, c'est un coup sur vingt qui passe à côté.":
    "What makes a blow miss. One more point is one blow in twenty that goes wide.",
  "Agilité": "Agility",
  AGI: "AGI",
  "+1 à l'initiative, à l'esquive, à la fuite et à la dissimulation": "+1 to initiative, dodging, fleeing and hiding",
  "Frapper le premier, et pouvoir partir. Qui commence a souvent déjà gagné — et fuir à temps, c'est un héros de plus au retour.":
    "Striking first, and being able to leave. Whoever starts has often already won, and fleeing in time is one more hero coming home.",
  Chance: "Luck",
  CHA: "LCK",
  "un critique tous les 4 points (seuil 20, puis 19, 18, 17 — jamais moins)":
    "one critical every 4 points (threshold 20, then 19, 18, 17, never lower)",
  "Le coup qui tombe au bon moment, et ce qu'on trouve en fouillant. La plus lente à payer, la seule qui puisse renverser un combat déjà perdu.":
    "The blow that lands at the right moment, and what you turn up while searching. The slowest to pay off, the only one that can turn a fight already lost.",
  Essence: "Essence",
  ESS: "ESS",
  "+1 d'Essence maximum": "+1 maximum Essence",
  "Le carburant des capacités d'archétype. Sans Essence, un Mage frappe avec un bâton.":
    "The fuel of an archetype's power. Without Essence, a Mage swings a stick.",

  /* --- Les archétypes (§3.1) ------------------------------------------------ */
  Guerrier: "Warrior",
  "Il encaisse, et il rend.": "He takes it, and he gives it back.",
  Roublard: "Rogue",
  "Il frappe une fois, dans le dos, et ça suffit.": "One strike, from behind, and that is enough.",
  Mage: "Mage",
  "Il paie en Essence ce que les autres paient en sang.": "He pays in Essence what others pay in blood.",
  "Rôdeur": "Ranger",
  "Il tire de loin et connaît la forêt.": "He shoots from far off and knows the woods.",
  Clerc: "Cleric",
  "Il tient debout ceux qui devaient tomber.": "He keeps standing those who should have fallen.",
  "Érudit": "Scholar",
  "Il regarde d'abord, et frappe où il faut.": "He looks first, then strikes where it counts.",

  /* --- Les états (§3.2) ----------------------------------------------------- */
  Saignement: "Bleeding",
  "2 dégâts par tour. Ne se soigne pas au repos.": "2 damage a turn. Rest will not mend it.",
  "vous met en sang": "leaves you bleeding",
  Poison: "Poison",
  "1 dégât par tour. Ne se soigne pas au repos : il faut un antidote.":
    "1 damage a turn. Rest will not mend it: you need an antidote.",
  "vous empoisonne": "poisons you",
  "Étourdi": "Stunned",
  "Étourdi : ce tour est perdu.": "Stunned: this turn is lost.",
  "vous étourdit": "stuns you",
  Aveugle: "Blinded",
  "−4 à tous les jets.": "−4 to every roll.",
  "vous aveugle": "blinds you",
  "Terrifié": "Terrified",
  "−2 à tous les jets.": "−2 to every roll.",
  "vous terrifie": "terrifies you",
  "Entravé": "Bound",
  "−2 en Agilité, et l'on ne fuit pas.": "−2 Agility, and no fleeing.",
  "vous entrave": "binds you",

  /* --- Les blessures graves (§3.2) ------------------------------------------ */
  "Jambe brisée": "Broken leg",
  "L'Agilité en pâtit, et la marche coûte plus cher.": "Agility suffers, and every mile costs more.",
  "Bras cassé": "Broken arm",
  "L'Attaque en pâtit.": "Attack suffers.",
  "Œil perdu": "Lost eye",
  "Les jets de perception et de tir en pâtissent.": "Spotting and shooting both suffer.",
  "Côtes enfoncées": "Caved ribs",
  "La Défense en pâtit ; respirer coûte.": "Defence suffers; breathing costs something.",
  "Main écrasée": "Crushed hand",
  "Tenir une arme à deux mains devient difficile.": "Holding a two-handed weapon becomes hard.",
  Commotion: "Concussion",
  "Tous les jets en pâtissent tant qu'elle dure.": "Every roll suffers while it lasts.",

  /* --- Les raretés, les emplacements ---------------------------------------- */
  Commun: "Common",
  Rare: "Rare",
  "Épique": "Epic",
  "Légendaire": "Legendary",
  Arme: "Weapon",
  "Main gauche": "Off hand",
  Armure: "Armour",
  Casque: "Helm",
  Bottes: "Boots",
  Amulette: "Amulet",
  "Anneau (I)": "Ring (I)",
  "Anneau (II)": "Ring (II)",
  Ceinture: "Belt",
  Cape: "Cloak",
  Anneau: "Ring",

  /* --- Fouiller (§3.6) ------------------------------------------------------ */
  Fouiller: "Search",
  "Le corps ne porte plus rien.": "The body carries nothing more.",
  Forcer: "Force it",
  "Le coffre est vide.": "The chest is empty.",
  "La cachette est vide.": "The hiding place is empty.",
  Accepter: "Accept",
  "On ne vous tend plus rien.": "Nothing more is held out to you.",
  "Dégager": "Clear it",
  "Il ne reste rien.": "Nothing is left.",

  /* --- Les marqueurs de carte (§4.2) ---------------------------------------- */
  Monstre: "Monster",
  Coffre: "Chest",
  Clef: "Key",
  "Porte verrouillée": "Locked door",
  "Quelqu'un": "Someone",
  Danger: "Danger",
  Ressource: "Supplies",
  "Énigme": "Riddle",
  "À revoir": "Come back",

  /* --- Les biomes (§4.1) ---------------------------------------------------- */
  "Forêt": "Forest",
  Montagne: "Mountain",
  Marais: "Marsh",
  "Terres sèches": "Dry lands",
  Ruines: "Ruins",
  Souterrain: "Underground",
  Ville: "Town",
  "Côte": "Coast",

  /* --- Les modes de mort (§3.5) --------------------------------------------- */
  Permadeath: "Permadeath",
  "Une vie. La mort ferme le héros pour toujours et laisse une trace dans le monde.":
    "One life. Death closes the hero for good and leaves a mark on the world.",
  "Héritage": "Legacy",
  "Le héros meurt pour de bon, mais l'aventure continue : un successeur de la même main reprend à la dernière ancre, avec la moitié de l'or et une relique du défunt. Une option, dépensée au départ.":
    "The hero dies for good, but the adventure goes on: an heir of the same making picks up at the last anchor, with half the gold and one relic of the dead. An option, spent when you set out.",
  Chronique: "Chronicle",
  "Trois reprises. Chacune coûte une cicatrice : −1 définitif sur une caractéristique que vous choisissez.":
    "Three second chances. Each costs a scar: a permanent −1 on a trait you choose.",

  /* --- L'échoppe (§8.2) ----------------------------------------------------- */
  "L'équipement": "Gear",
  "Ce qu'on emporte au corps. Il compte dans l'indice que les aventures regardent à la porte : mieux armé, on entre dans des histoires plus dures — et l'on n'entre plus dans les faciles.":
    "What you carry on your body. It counts towards the rating adventures check at the door: better armed, you walk into harder stories, and the easy ones close to you.",
  "Les consommables": "Supplies",
  "De quoi tenir : manger, se soigner, y voir. Rien de tout cela ne rend plus fort ; ça évite seulement de mourir bêtement.":
    "What keeps you going: food, healing, light. None of it makes you stronger; it only keeps you from dying for nothing.",
  "Les options d'aventure": "Adventure options",
  "Elles se choisissent au départ d'une aventure et s'y dépensent. Une par aventure, jamais rétroactive : on ne rachète pas une mort déjà arrivée.":
    "Chosen as an adventure begins, and spent there. One per adventure, never after the fact: a death already suffered cannot be bought back.",
  "Le confort": "Comfort",
  "Ce qui agrandit le compte pour de bon. Acheté une fois, gardé toujours — même si l'abonnement s'arrête.":
    "What enlarges the account for good. Bought once, kept always, even if the subscription stops.",

  /* --- Les fragments (§8) --------------------------------------------------- */
  "Fragments achetés": "Fragments bought",
  "Aventure terminée": "Adventure finished",
  "Boss abattu": "Boss felled",
  "Acheté à l'échoppe": "Bought at the shop",
  Remboursement: "Refund",
  Ajustement: "Adjustment",

  /* --- Le repos (§3.4) ------------------------------------------------------ */
  Souffler: "Catch your breath",
  "1 h · rien à payer · 5 % des PV · ni fatigue ni essence": "1 h · nothing to pay · 5% of HP · no fatigue, no essence",
  Halte: "Halt",
  "1 h · 1 ration · 10 % des PV · −1 fatigue": "1 h · 1 ration · 10% of HP · −1 fatigue",
  "Repos long": "Long rest",
  "8 h · 2 rations · 40 % des PV · fatigue remise à zéro": "8 h · 2 rations · 40% of HP · fatigue cleared",
  Camp: "Camp",
  "8 h · 2 rations · kit requis · à l'abri des rencontres de nuit": "8 h · 2 rations · kit needed · safe from night encounters",
  "Dormir au sanctuaire": "Sleep at the sanctuary",
  "8 h · 1 ration · tout revient · poison et saignement dissipés":
    "8 h · 1 ration · everything comes back · poison and bleeding cleared",

  /* --- Les caps (§4.5) ------------------------------------------------------ */
  "plein est": "due east",
  "au nord-est": "north-east",
  "au nord-ouest": "north-west",
  "plein ouest": "due west",
  "au sud-ouest": "south-west",
  "au sud-est": "south-east",

  /* --- La faim, les statuts, les genres ------------------------------------- */
  "Rassasié": "Fed",
  Creux: "Peckish",
  "Affamé": "Starving",
  "Épuisé par la faim": "Worn out by hunger",
  Famine: "Famine",
  "En cours": "In progress",
  "Terminée": "Finished",
  Mort: "Dead",
  "Abandonnée": "Abandoned",
  "Cicatrice à désigner": "Scar to choose",
  Fantasy: "Fantasy",
  "Post-apocalyptique": "Post-apocalyptic",
  Gothique: "Gothic",
  "Science-fiction": "Science fiction",
  Contemporain: "Contemporary",

  /* --- Les étapes de l'atelier (§12) ---------------------------------------- */
  "On invente le monde": "Inventing the world",
  "On dessine la carte": "Drawing the map",
  "On perce les chemins": "Cutting the paths",
  "On peuple : objets, bêtes et gens": "Peopling it: objects, beasts and folk",
  "On lui donne ses noms et ses usages": "Giving it its names and its uses",
  "On cache les clefs — et on vérifie qu'on peut ressortir": "Hiding the keys, and checking there is a way out",
  "On écrit": "Writing",
  "On relit tout": "Reading it all back",
  "On commande les images": "Ordering the pictures",

  /* --- Les verbes du sac (§3.3, §3.7, §3.8) --------------------------------- */
  Manger: "Eat",
  Boire: "Drink",
  "Se panser": "Bind up",
  "rend {formule} PV": "restores {formule} HP",
  "+{n} essence": "+{n} essence",
  "soigne {etat}": "cures {etat}",
  "un repas": "a meal",
  "révèle ce qui est caché ici": "reveals what is hidden here",
  "porte sur la carte ce qui est à {n} case": "maps what lies {n} tile away",
  "porte sur la carte ce qui est à {n} cases": "maps what lies {n} tiles away",
  "referme une blessure grave": "closes one grave wound",
  "dissipe tous les états": "clears every condition",
  "quitte un combat sans jet — pendant un combat seulement": "leaves a fight with no roll, in a fight only",
  "{quoi} pendant {h} h": "{quoi} for {h} h",

  /* --- Les mesures ---------------------------------------------------------- */
  "immédiat": "at once",
  "{h} h": "{h} h",
  "{j} j": "{j} d",
  "{j} j {h} h": "{j} d {h} h",

  /* --- Le pupitre (§13.2) --------------------------------------------------- */
  "La langue": "Language",
  "L'interface et le carnet de bord suivent votre langue. Une aventure, elle, reste dans celle où elle a été écrite.":
    "The interface and the logbook follow your language. An adventure stays in the one it was written in.",
  "La lampe": "The lamp",

  /* --- Le carnet de bord (§13.1) -------------------------------------------- */
  "L'aventure commence : {titre}.": "The adventure begins: {titre}.",
  "L'aventure s'achève.": "The adventure ends.",
  "Vous prenez vos repères : ce lieu servira de point de reprise.":
    "You take your bearings: this place will serve as a return point.",
  "Vous n'êtes pas seul ici.": "You are not alone here.",
  "Reprise à l'ancre. Cicatrice : {quoi}.": "Back at the anchor. Scar: {quoi}.",
  "{defunt} est tombé — {cause}. {successeur} reprend la route à l'ancre.":
    "{defunt} has fallen, {cause}. {successeur} takes the road again from the anchor.",
  "Mort : {cause}.": "Death: {cause}.",
  "La faim se fait sentir.": "Hunger makes itself felt.",
  "La famine vous prend des forces.": "Famine is taking your strength.",
  "La fatigue pèse sur tous vos gestes.": "Fatigue weighs on every move.",
  "Vous ne tenez plus debout.": "You cannot stay on your feet.",
  "Votre flamme s'éteint.": "Your flame goes out.",
  "Ce qui vous portait s'éteint.": "What was carrying you fades.",
  "Le corps parle : {quoi}.": "The body speaks: {quoi}.",
  "Repas.": "A meal.",
  "{repos} : {pv} PV.": "{repos}: {pv} HP.",
  "{repos} : {pv} PV. On vous a soigné : {etats}.": "{repos}: {pv} HP. You were tended: {etats}.",
  "Le rebouteux referme ce qui pouvait l'être ({blessure}) : {prix} or.":
    "The bonesetter closes what could be closed ({blessure}): {prix} gold.",
  "{nom} : {effet}.": "{nom}: {effet}.",
  "sans effet": "no effect",
  "+{n} PV": "+{n} HP",
  "{etats} soigné(s)": "{etats} cured",
  repas: "a meal",
  "{blessure} refermée": "{blessure} closed",
  "{etats} dissipé(s)": "{etats} cleared",
  "béni pour {heures} h": "blessed for {heures} h",
  "{n} chose(s) que personne n'avait vue": "{n} thing(s) nobody had seen",
  "rien de caché ici": "nothing hidden here",
  "{n} case(s) portées sur la carte": "{n} tile(s) put on the map",
  "{nom} : {heures} h de lumière.": "{nom}: {heures} h of light.",
  "Vous étouffez la flamme.": "You smother the flame.",
  "Vous fouillez {quoi}.": "You search {quoi}.",
  "Vous fouillez {quoi}. {or} or.": "You search {quoi}. {or} gold.",
  "On vous tend {quoi}.": "{quoi} is held out to you.",
  "On vous tend {quoi}. {or} or.": "{quoi} is held out to you. {or} gold.",
  "{combien} objet(s) pris dans {quoi}.": "{combien} item(s) taken from {quoi}.",
  "Vous fouillez les lieux : {combien} chose(s) que personne n'avait vue.":
    "You search the place: {combien} thing(s) nobody had seen.",
  "Vous fouillez les lieux, méthodiquement. Il n'y a rien de plus.":
    "You search the place, methodically. There is nothing more.",
  "Vous fouillez à tâtons, et vous ne trouvez rien : il faudrait de la lumière.":
    "You grope about and find nothing: you would need light.",
  "L'inscription vous donne raison.": "The inscription grants you right.",
  "Vous tournez autour de l'inscription, sans trouver.": "You circle the inscription, and do not find it.",
  "{quoi} — {stat} {de}+{bonus} contre {dc} : {issue}.": "{quoi}: {stat} {de}+{bonus} against {dc}, {issue}.",
  "réussi": "made it",
  "raté": "failed",
  "{nom} : vous le voyez à temps ({jet}+{bonus} contre {dc}).":
    "{nom}: you see it in time ({jet}+{bonus} against {dc}).",
  "{nom} : {jet}+{bonus} contre {dc}, {degats} dégâts": "{nom}: {jet}+{bonus} against {dc}, {degats} damage",
  "{degats} dégâts.": "{degats} damage.",
  "État : {etat}.": "Condition: {etat}.",
  "Combat engagé : {combien} adversaire(s).": "Fight joined: {combien} opponent(s).",
  "Victoire en {tours} tour(s). {or} or.": "Won in {tours} turn(s). {or} gold.",
  "Vous fuyez.": "You flee.",
  "Il reste un corps à fouiller.": "A body is left to search.",

  /* --- Les refus du serveur (§13.3) ----------------------------------------- */
  "Cette adresse a déjà un compte. Connectez-vous plutôt.": "That address already has an account. Sign in instead.",
  "Cette adresse ne ressemble pas à une adresse.": "That does not look like an address.",
  "Il faut au moins dix caractères — c'est la seule chose qui protège vos héros.":
    "Ten characters at least. It is the only thing protecting your heroes.",
  "Ni cette adresse ni ce mot de passe ne conviennent.": "Neither that address nor that password will do.",
  "Votre session s'est refermée. Reconnectez-vous.": "Your session has closed. Sign in again.",
  "Ceci ne vous appartient pas.": "This is not yours.",
  "Il n'y a rien ici.": "There is nothing here.",
  "Un nom, entre deux et trente-deux caractères.": "A name, between two and thirty-two characters.",
  "Cet archétype n'existe pas.": "That archetype does not exist.",
  "Cette répartition ne tient pas : huit points, quatre au plus sur une même caractéristique.":
    "That spread does not hold: eight points, four at most on any one trait.",
  "Il reste des points. Ils ne se garderont pas : il n'y a pas de « plus tard » où les dépenser.":
    "Points are left over. They will not keep: there is no later in which to spend them.",
  "Quatre points au maximum sur une seule caractéristique.": "Four points at most on a single trait.",
  "Votre abonnement ne tient pas un héros vivant de plus. Retirez-en un, ou attendez qu'il meure.":
    "Your subscription will not hold one more living hero. Retire one, or wait for one to die.",
  "Ce héros n'est plus en état de partir.": "This hero is in no state to set out.",
  "Cette caractéristique n'existe pas.": "That trait does not exist.",
  "Ce corps ne peut plus porter de cicatrice. La Chronique s'arrête ici.":
    "This body can bear no more scars. The Chronicle ends here.",
  "Vous êtes trop bien équipé pour cette aventure. Ce ne serait pas un défi, ce serait une visite.":
    "You are too well equipped for this adventure. It would be no challenge, only a visit.",
  "Votre équipement est en dessous de ce que cette aventure demande. Vous pouvez entrer quand même, par pacte.":
    "Your gear falls below what this adventure asks. You may still enter, by pact.",
  "Ce mode de jeu n'existe pas.": "That mode does not exist.",
  "La Chronique demande un abonnement. En permadeath, la mort est définitive — c'est le jeu tel qu'il est écrit.":
    "The Chronicle asks for a subscription. In permadeath, death is final: the game as it was written.",
  "L'Héritage se dépense au départ, et vous n'en avez plus au coffre. Il s'achète à l'échoppe.":
    "Legacy is spent when you set out, and your vault has none left. It is bought at the shop.",
  "Ce héros est déjà en aventure. On ne se dédouble pas.": "This hero is already out there. Nobody is in two places.",
  "Quelqu'un vous barre le passage. On ne s'en va pas, et on ne s'attarde pas : il faut en découdre.":
    "Someone bars the way. No leaving, no lingering: it has to be settled.",
  "Il n'y a personne à affronter ici.": "There is nobody to face here.",
  "Vous avez écouté tout ce que le compte gratuit permet aujourd'hui. La lecture par le navigateur, elle, ne se compte pas.":
    "You have listened to all a free account allows today. Your browser's own voice is not counted.",
  "Cette aventure n'a pas de premier nœud : elle est inutilisable.":
    "This adventure has no opening node: it cannot be played.",
  "Cette partie est terminée.": "This run is over.",
  "Votre héros est tombé. Désignez la cicatrice qu'il gardera avant de repartir.":
    "Your hero has fallen. Name the scar he will keep before setting out again.",
  "Rien à reprendre ici.": "Nothing to resume here.",
  "Aucune ancre où revenir.": "No anchor to return to.",
  "Cette partie a perdu son nœud courant.": "This run has lost its current node.",
  "Ce nœud n'existe pas dans cette aventure.": "That node does not exist in this adventure.",
  "Il vous manque quelque chose pour ce chemin.": "Something is missing for this path.",
  "Plus une seule ration.": "Not one ration left.",
  "Pas assez de rations pour ce repos.": "Not enough rations for that rest.",
  "Un camp demande un kit de campement.": "A camp asks for a camping kit.",
  "Cela ne se fait que dans un sanctuaire. Il y en a un par région — la carte les garde en mémoire.":
    "That is done only in a sanctuary. There is one per region, and the map remembers them.",
  "Ce repos n'existe pas.": "That rest does not exist.",
  "Cet objet ne se lit pas.": "This object is not read.",
  "Cela se lit avant d'engager, pas au milieu des coups.": "This is read before engaging, not among the blows.",
  "Ce mot-là n'a de sens qu'en plein combat.": "That word only means something in the middle of a fight.",
  "Vous n'avez aucune blessure grave à refermer — gardez-le.": "You have no grave wound to close. Keep it.",
  "Vous ne portez pas cette blessure-là.": "You do not carry that wound.",
  "Le rebouteux ne travaille pas à crédit.": "The bonesetter does not work on credit.",
  "Cela n'éclaire rien.": "That lights nothing.",
  "Vous n'avez rien d'allumé.": "You have nothing lit.",
  "Cet objet ne porte aucun sort.": "This object carries no spell.",
  "Votre dos ne portera pas ça. Posez quelque chose.": "Your back will not take that. Put something down.",
  "Votre sac est plein. Posez quelque chose, ou trouvez un contenant.":
    "Your bag is full. Put something down, or find a container.",
  "Vous n'avez pas cet objet.": "You do not have that object.",
  "Cet objet n'existe pas.": "That object does not exist.",
  "Cet objet ne se consomme pas.": "This object is not consumed.",
  "Cet objet ne se porte pas là.": "This object is not worn there.",
  "Il n'y a rien à cet emplacement.": "There is nothing in that slot.",
  "Vos deux mains sont déjà prises par cette arme.": "Both your hands are already taken by that weapon.",
  "Cette quantité n'a pas de sens.": "That amount makes no sense.",
  "Vous ne vous battez contre personne.": "You are fighting nobody.",
  "Pas pendant un combat.": "Not during a fight.",
  "Ce combat est fini.": "This fight is over.",
  "Ce n'est pas votre tour.": "It is not your turn.",
  "Cette cible n'est pas là.": "That target is not there.",
  "Cette action n'existe pas.": "That action does not exist.",
  "Pas assez d'Essence.": "Not enough Essence.",
  "Vous avez déjà usé de votre capacité dans ce combat.": "You have already used your power in this fight.",
  "Votre archétype n'a pas cette capacité.": "Your archetype has no such power.",
  "Le tour est perdu.": "The turn is lost.",
  "Il faut être caché pour cela.": "You must be hidden for that.",
  "Il n'y a plus personne devant vous.": "There is nobody left in front of you.",
  "Ce genre n'est pas au catalogue.": "That genre is not in the catalogue.",
  "Une aventure fait entre huit cents et mille nœuds.": "An adventure runs between eight hundred and a thousand nodes.",
  "Une aventure est déjà en fabrication pour vous.": "An adventure is already being made for you.",
  "La fabrication s'est arrêtée. Rien n'a été facturé, vous pouvez relancer.":
    "The making stopped. Nothing was charged; you can start it again.",
  "Vous avez atteint votre quota du jour.": "You have reached your quota for today.",
  "Votre quota du mois est épuisé.": "Your quota for the month is spent.",
  "Cette aventure est déjà en base.": "This adventure is already in the database.",
  "Cette aventure ne passe pas le validateur.": "This adventure does not pass the validator.",
  "Le solveur n'a pas pu prouver qu'on peut la finir. Elle ne sera pas jouée.":
    "The solver could not prove it can be finished. It will not be played.",
  "Quelqu'un y joue en ce moment.": "Someone is playing it right now.",
  "La requête n'était pas lisible.": "The request could not be read.",
  "Quelque chose a cassé de notre côté. Rien n'est perdu : rechargez la page.":
    "Something broke on our side. Nothing is lost: reload the page.",
  "Quelque chose a cassé. Rien n'est perdu : rechargez la page.":
    "Something broke. Nothing is lost: reload the page.",
  "Quelque chose a été refusé ({code}).": "Something was refused ({code}).",
  "Le serveur n'a pas répondu. Vérifiez votre connexion.": "The server did not answer. Check your connection.",
  "Vous l'avez déjà fouillée.": "You have already searched it.",
  "C'est fermé, et vous n'avez pas la clef. Notez l'endroit : elle est quelque part dans cette région.":
    "It is locked, and you have no key. Mark the spot: the key is somewhere in this region.",
  "Ce n'est pas le mot. Vous y avez perdu une heure — mais l'inscription ne s'efface pas.":
    "That is not the word. You have lost an hour over it, but the inscription does not fade.",
  "L'inscription est illisible. Signalez-le : ce n'est pas de votre fait.":
    "The inscription cannot be read. Report it: this one is not your doing.",
  "Il faut d'abord l'ouvrir.": "It has to be opened first.",
  "Il n'y a plus ça dedans.": "That is no longer inside.",
  "Vous avez déjà fouillé les lieux. Il n'y a rien de plus à trouver ici.":
    "You have already searched the place. There is nothing more to find here.",
  "On n'écrit pas sur ce qu'on n'a pas vu. Allez-y d'abord, ou apercevez-le de loin.":
    "You do not write on what you have not seen. Go there first, or glimpse it from afar.",
  "Ce marqueur n'existe pas.": "That marker does not exist.",
  "Cent vingt signes au plus — une note de carte se lit d'un coup d'œil.":
    "A hundred and twenty characters at most: a map note is read at a glance.",
  "On ne vote pas pour sa propre note.": "You do not vote for your own note.",
  "Cet article n'est pas à l'étal.": "That item is not on the stall.",
  "Pas assez de fragments. On en gagne en terminant une aventure — vingt — et en abattant un boss — cent.":
    "Not enough fragments. You earn them by finishing an adventure, twenty, and by felling a boss, a hundred.",
  "Votre coffre n'a plus une ligne de libre. Videz-en une, ou achetez une étagère de plus. Rien n'a été débité.":
    "Your vault has not one free line. Clear one, or buy another shelf. Nothing was charged.",
  "Le coffre n'en contient pas autant.": "The vault does not hold that many.",
  "Ce héros est en route. On prépare son héros avant de partir, on assume ensuite.":
    "This hero is on the road. You outfit a hero before setting out, and live with it after.",
  "Ce héros est mort. Son coffre reste au compte ; lui, non.":
    "This hero is dead. The vault stays with the account; he does not.",
  "Une option d'aventure ne se porte pas : elle se dépense au départ.":
    "An adventure option is not carried: it is spent when you set out.",
  "Cette formule n'existe pas.": "That offer does not exist.",
  "La caisse n'est pas ouverte pour le moment. Rien n'a été débité.":
    "The till is not open at the moment. Nothing was charged.",
  "Le paiement n'a pas abouti. Rien n'a été débité.": "The payment did not go through. Nothing was charged.",
  "Cette confirmation de paiement n'est pas authentique.": "That payment confirmation is not genuine.",

  /* --- Les écrans, le récit de combat et la carte (§13, lot 8) --------------- */

  " — {pourcent} % en sont repartis vivants": " : {pourcent} % walked away alive",
  "({ipe} pour un plafond de {plafond}.)": "({ipe} against a ceiling of {plafond}.)",
  "({ipe} pour un seuil de {seuil}.)": "({ipe} against a floor of {seuil}.)",
  "+{bonus} en défense jusqu'à votre prochain tour — vous ne frappez pas":
    "+{bonus} to defence until your next turn; you do not strike",
  "+{bonus} à l'esquive, et l'esquive reste gardée — vous ne frappez pas":
    "+{bonus} to dodging, and the dodge stays held; you do not strike",
  ", avec les formules d'abonnement.": ", along with the subscription plans.",
  ", et à un prix : une cicatrice, c'est-à-dire": ", and at a price: a scar, which means",
  ", puis ": ", then ",
  "1 ration": "1 ration",
  ": vos héros vivants le restent, votre coffre reste lisible et se vide comme vous voulez. Vous ne pouvez simplement plus en ajouter tant que vous êtes au-dessus du plafond du compte gratuit.":
    ": your living heroes stay alive, your vault stays readable and empties as you please. You simply cannot add to it while you are above the free account's ceiling.",
  "Acheter": "Buy",
  "Acte {n}": "Act {n}",
  "Adresse": "Address",
  "Affaires en suspens": "Unfinished business",
  "Agrandir le texte": "Larger text",
  "Ajouter un point de {quoi}": "Add a point of {quoi}",
  "Allumer": "Light",
  "Ancre": "Anchor",
  "Aperçu — {biome}{repere}": "Glimpsed: {biome}{repere}",
  "Archiviste": "Archivist",
  "Armé à": "Armed at",
  "Attaque puissante": "Heavy attack",
  "Attaquer": "Attack",
  "Au coffre": "In the vault",
  "Au coffre — {pris} / {total} lignes": "In the vault: {pris} / {total} lines",
  "Aucun argent ne circule ici": "No money changes hands here",
  "Aucun choix ne se prend tout seul : la lecture s'arrête à l'amorce, et c'est à vous. Toucher la page coupe la voix.":
    "No choice is ever taken for you: the reading stops at the last line, and the rest is yours. Touching the page cuts the voice.",
  "Aucun dé n'est lancé dans votre dos. Celui qui vous tuera non plus.":
    "No die is rolled behind your back. Not even the one that kills you.",
  "Aucun héros vivant : le coffre attend.": "No living hero: the vault waits.",
  "Autant de héros vivants que vous voulez.": "As many living heroes as you like.",
  "Aventure sans titre": "Untitled adventure",
  "Aventures": "Adventures",
  "Aventures par mois :": "Adventures per month:",
  "Blessure grave — {blessure} : {effet} Elle repartira avec lui.":
    "Grave wound, {blessure}: {effet} It leaves with him.",
  "Blessure grave — {blessure} : {effet} Elle repartira avec vous.":
    "Grave wound, {blessure}: {effet} It leaves with you.",
  "Blessures graves": "Grave wounds",
  "Boire, panser": "Drink, bind",
  "C'est parti. Comptez une demi-heure : fermez l'onglet, allez vivre, on vous préviendra à la table.":
    "It is under way. Count on half an hour: close the tab, go and live, we will tell you at the table.",
  "C'est votre palier. Payer à nouveau le prolonge.": "This is your tier. Paying again extends it.",
  "Caisse de développement": "Development till",
  "Ce carnet n'est plus ouvert": "This journal is no longer open",
  "Ce choix ne se tire pas au sort à votre place : c'est le vôtre, il est irréversible, et il vous suivra jusqu'à la fin de ce héros.":
    "This choice is not drawn for you: it is yours, it cannot be undone, and it will follow you to the end of this hero.",
  "Ce héros est trop bien armé pour cette histoire : elle n'aurait plus rien à lui opposer.":
    "This hero is too well armed for this story: it would have nothing left to put in his way.",
  "Ce lieu est noir : votre flamme y sert à quelque chose":
    "This place is dark: your flame is worth something here",
  "Ce n'est pas un avis, c'est ce qu'on a vu en jouant : un héros qui néglige l'Attaque perd un combat sur quatre de plus, quel que soit son archétype — elle décide si le coup passe, puis de ce qu'il coûte à l'autre. Ce héros-là, vous ne le jouerez qu'une fois.":
    "This is not an opinion, it is what the bench shows: a hero who neglects Attack loses one fight in four more than the others, whatever the archetype. It decides whether the blow lands, then what it costs the one facing you. And this hero, you will only play once.",
  "Ce n'est pas votre tour — l'adversaire joue.": "It is not your turn: the other side is playing.",
  "Ce qu'il a vu du monde": "What he saw of the world",
  "Ce qu'il aura vu": "What he will have seen",
  "Ce qu'il vaut au combat s'apprend en le frappant.": "What it is worth in a fight is learned by hitting it.",
  "Ce qu'il y a à fouiller ici": "What there is to search here",
  "Ce qu'on affiche": "What is shown",
  "Ce qu'on en sait": "What is known of it",
  "Ce que d'autres ont laissé ici": "What others left here",
  "Ce que vous décidez ici, il le portera jusqu'au bout. Il ne gagnera pas de niveaux, il ne rattrapera rien : au dernier combat comme au premier, il aura exactement ces six nombres.":
    "What you decide here, he carries to the end. He gains no levels, he catches nothing up: in the last fight as in the first, he will have exactly these six numbers.",
  "Ce que vous laissez reste ici. Rien ne s'évapore.": "What you leave stays here. Nothing evaporates.",
  "Ce que vous portez": "What you are carrying",
  "Ce que ça donnera": "What it will come to",
  "Ce qui vous porte": "What carries you",
  "Ce serveur n'a pas de prestataire de paiement : il tient une caisse locale, pour qu'on puisse voir l'écran d'achat fonctionner sans payer quoi que ce soit. Rien ne sera débité, nulle part, à personne.":
    "This server has no payment provider: it keeps a local till, so the buying screen can be seen working without anyone paying anything. Nothing will be charged, anywhere, to anyone.",
  "Celui qu'on lira sur la pierre": "The one that will be read on the stone",
  "Ces six nombres ont été fixés à la création et ne changeront plus jamais. Toute la puissance qui vous reste à gagner est dans ce que vous porterez.":
    "These six numbers were set at creation and will never change again. All the power left to gain is in what you carry.",
  "Cette note m'a servi": "This note helped me",
  "Cette page n'existe pas": "This page does not exist",
  "Cette partie n'a plus de nœud courant.": "This run has no current node left.",
  "Ceux d'avant": "Those who came before",
  "Ceux qui ne sont pas revenus. Chacun n'a eu qu'une vie, comme vous n'en aurez qu'une : aucun de ces noms ne s'effacera.":
    "Those who did not come back. Each had one life, as you will have one: not a single name here will be erased.",
  "Chacune attend un héros de sa taille. Trop peu armé, vous n'y verrez pas le troisième jour ; trop bien armé, elle n'aura plus rien à vous opposer.":
    "Each one waits for a hero its own size. Too lightly armed and you will not see the third day; too well armed and it will have nothing left to put in your way.",
  "Charge": "Load",
  "Choisir une aventure": "Choose an adventure",
  "Chroniqueur": "Chronicler",
  "Cimetière": "Graveyard",
  "Cinquante lignes de coffre, au lieu de vingt.": "Fifty vault lines instead of twenty.",
  "Combat": "Fight",
  "Commander une aventure": "Commission an adventure",
  "Commencer": "Begin",
  "Continuer": "Continue",
  "Coup critique. Vous ouvrez {cible} : {degats} dégâts.": "Critical hit. You open {cible} up: {degats} damage.",
  "Coup critique. {acteur} ouvre {cible} : {degats} dégâts.":
    "Critical hit. {acteur} opens {cible} up: {degats} damage.",
  "Coup critique. {acteur} vous ouvre : {degats} dégâts.": "Critical hit. {acteur} opens you up: {degats} damage.",
  "Critique sur": "Critical on",
  "D'abord, qui il est": "First, who he is",
  "D'autres mains ({combien})": "Other hands ({combien})",
  "D'où viennent vos fragments, et où ils sont allés": "Where your shards came from, and where they went",
  "DESTINIA": "DESTINIA",
  "Dans le noir · {malus} à tous les jets": "In the dark · {malus} to every roll",
  "Des fragments": "Shards",
  "Destinia — accueil": "Destinia: home",
  "Destinia — le livre dont on ne sort qu'une fois":
    "Destinia: the book you only walk out of once",
  "Dites un genre, un ton, une longueur. Le monde s'écrit sans vous — sa carte, ses gens, ses chemins et ce qu'on y a caché — et vous serez le premier à le lire.":
    "Name a genre, a tone, a length. The world writes itself without you, its map, its people, its roads and what was hidden along them, and you will be the first to read it.",
  "Douze heures de jeu tiennent dans une colonne de phrases. On peut les donner à lire.":
    "Twelve hours of play fit into one column of sentences. They can be handed over to be read.",
  "Défaite, au tour {tours}.": "Defeat, on turn {tours}.",
  "Désigner la cicatrice": "Name the scar",
  "Effacer": "Erase",
  "Elle ne coûte rien et ne compte dans aucun quota. Terminez-la — ou tombez en chemin, ce qui se lit aussi.":
    "It costs nothing and counts against no quota. Finish it, or fall on the way, which reads just as well.",
  "Elle s'achète à l'échoppe": "It can be bought at the shop",
  "Elle s'écrit. Aucun écran d'attente à surveiller : revenez quand vous voulez, elle sera là — et elle sera la même que pour tout le monde.":
    "It is being written. No waiting screen to watch: come back when you like, it will be here, and it will be the same one everybody else gets.",
  "Emplacements": "Slots",
  "Emporter": "Take along",
  "En créer un": "Create one",
  "En découdre": "Have it out",
  "En fabrication": "Being made",
  "En file": "In the queue",
  "Ensuite, les huit points": "Then, the eight points",
  "Entrer": "Enter",
  "Entrer moins bien armé que prévu est permis — on vous le demande en clair, et vous en portez le prix. Entrer surarmé ne l'est pas : ce ne serait plus une aventure, ce serait une visite guidée.":
    "Walking in more lightly armed than expected is allowed: you are asked in plain words, and you carry the price. Walking in overarmed is not; that would no longer be an adventure, it would be a guided tour.",
  "Entrer quand même, moins bien armé que ce que l'histoire attend — elle ne s'adoucira pas pour autant.":
    "Go in anyway, more lightly armed than the story expects; it will not soften for that.",
  "Esquiver": "Dodge",
  "Faim": "Hunger",
  "Faire soigner": "Have it treated",
  "Fatigue": "Fatigue",
  "Fermer le livre": "Close the book",
  "Fermé : il vous faut {quoi}": "Locked: you need {quoi}",
  "Feuillets de ce nœud": "Leaves of this node",
  "Fouiller les lieux": "Search the place",
  "Frapper": "Strike",
  "Fuir": "Flee",
  "Garder de quoi esquiver": "Hold back a dodge",
  "Genre": "Genre",
  "Grand": "Large",
  "Gratuit": "Free",
  "Heure": "Hour",
  "Huit aventures écrites pour vous par mois, au lieu d'une.":
    "Eight adventures written for you each month instead of one.",
  "Héros": "Heroes",
  "Héros vivants :": "Living heroes:",
  "Il aura tenu": "He lasted",
  "Il fait jour": "It is day",
  "Il fait nuit": "It is night",
  "Il faut un héros vivant pour entrer.": "You need a living hero to go in.",
  "Il n'y a plus de chemin d'ici. C'est une fin.": "There is no road left from here. This is an ending.",
  "Il ne reviendra pas. Son nom est au cimetière, et une trace reste dans le monde, à l'endroit exact où il est tombé — les prochains la trouveront.":
    "He is not coming back. His name is in the graveyard, and a trace stays in the world at the exact spot where he fell; whoever comes next will find it.",
  "Il referme une blessure, et une seule à la fois. Il faut la payer : {or} or en bourse.":
    "He closes one wound, and only one at a time. It has to be paid for: {or} gold in your purse.",
  "Il reste {n} point": "{n} point still to spend",
  "Il reste {n} points": "{n} points still to spend",
  "Il vous manque ce qu'il faut avoir fait avant": "You are missing what had to be done first",
  "Il y a du nouveau.": "There is something new.",
  "Ils ne périment pas, et ils ne se remboursent pas en argent. On en gagne aussi en jouant.":
    "They do not expire, and they are not refunded in money. They are also earned by playing.",
  "Immense": "Huge",
  "Indice de puissance": "Power rating",
  "Indice de puissance de l'équipement — c'est ce qu'une aventure regarde à la porte.":
    "Equipment power rating: this is what an adventure looks at on the doorstep.",
  "Initiative": "Initiative",
  "J'ai déjà un compte": "I already have an account",
  "Je n'ai pas encore de compte": "I do not have an account yet",
  "Jeter": "Drop",
  "Jour": "Day",
  "Jusqu'au {date}. Payer à nouveau avant cette date PROLONGE : vous ne perdez pas les jours qui restent.":
    "Until {date}. Paying again before that date EXTENDS it: you do not lose the days that remain.",
  "L'Héritage est une option, dépensée au départ, et votre coffre n'en contient pas.":
    "Legacy is an option, spent at the start, and your vault holds none.",
  "L'adversaire": "The enemy",
  "L'aiguille tient {cap} — à {n} case d'ici.": "The needle holds {cap}, {n} tile from here.",
  "L'aiguille tient {cap} — à {n} cases d'ici.": "The needle holds {cap}, {n} tiles from here.",
  "L'aventure de la semaine": "This week's adventure",
  "L'aventure est finie, et le héros est vivant. Il gardera ce qu'il porte : c'est avec ça qu'il entrera dans la suivante — et c'est aussi ce qui décidera de celles où on le laissera entrer.":
    "The adventure is over and the hero is alive. He keeps what he carries: that is what he walks into the next one with, and that is also what decides which ones will let him in.",
  "L'aventure que vous ouvrirez n'existait pas la veille. Aucun guide en ligne ne vous dira où aller : ce que vous saurez de ce monde, vous l'aurez appris en y marchant. Et":
    "The adventure you open did not exist the day before. No walkthrough will tell you where to go: what you know of this world, you will have learned by walking it. And",
  "L'heure qu'il est dans le monde": "The hour it is in the world",
  "L'indice de puissance de l'équipement : ce que l'aventure attend d'un héros à la porte.":
    "The equipment power rating: what the adventure expects of a hero on the doorstep.",
  "L'ouvrir au catalogue": "Open it in the catalogue",
  "L'échange": "The exchange",
  "L'échange est engagé — tour {tour}. Les coups déjà portés sont au journal ; ceux qui viennent s'écriront ici.":
    "The exchange is under way, turn {tour}. The blows already struck are in the log; the ones to come will be written here.",
  "L'échoppe": "The shop",
  "La Chronique : une mort se paie d'une cicatrice — −1 définitif sur une caractéristique que vous choisissez — au lieu de fermer le héros pour toujours. Trois fois, puis c'est fini.":
    "The Chronicle: a death costs a scar, a permanent −1 on a trait you choose, instead of closing the hero for good. Three times, then it is over.",
  "La Chronique demande un abonnement.": "The Chronicle needs a subscription.",
  "La Chronique vous laisse repartir de la dernière ancre —": "The Chronicle lets you set off again from the last anchor,",
  "La caisse n'est pas ouverte pour le moment. Les prix sont ci-dessous, mais rien ne peut être payé — et rien ne sera débité.":
    "The till is not open at the moment. The prices are below, but nothing can be paid, and nothing will be charged.",
  "La carte": "The map",
  "La carte est vierge. Elle se dessinera au fur et à mesure — un hexagone n'existe que lorsqu'on l'a vu.":
    "The map is blank. It draws itself as you go: a hex exists only once it has been seen.",
  "La carte, telle qu'il l'a levée": "The map, as he drew it",
  "La colonne suit : elle garde le même nombre de caractères par ligne, et le découpage en feuillets se refait pour la nouvelle taille.":
    "The column follows: it keeps the same number of characters per line, and the leaves are cut again for the new size.",
  "La note": "The note",
  "La publier": "Publish it",
  "La souscription": "Subscription",
  "La table": "The table",
  "La taille du texte": "Text size",
  "La veillée est faite pour lire au lit, la lampe basse. Le parchemin est fait pour tout le reste.":
    "Vigil is for reading in bed with the lamp low. Parchment is for everything else.",
  "La voix": "The voice",
  "La voix sans compte de nœuds, et la carte à exporter en grand.":
    "The voice with no node count, and the map to export large.",
  "Laissez vide pour qu'il l'invente": "Leave it empty and it will be invented",
  "Le carnet de bord": "The logbook",
  "Le catalogue": "The catalogue",
  "Le catalogue est vide. Commandez-en une : le temps d'un repas, elle vous attendra ici.":
    "The catalogue is empty. Commission one: in the time it takes to eat, it will be waiting here.",
  "Le cimetière": "The graveyard",
  "Le coffre": "The vault",
  "Le coffre est vide. Ce qu'on achète à l'échoppe atterrit ici.":
    "The vault is empty. What you buy at the shop lands here.",
  "Le corps": "The body",
  "Le dé roule à l'écran et le chiffre à battre est écrit à côté. Quand un coup vous tue, vous savez lequel et de combien il s'en est fallu — et vous pouvez le faire rejouer à l'identique, pour le montrer à quelqu'un.":
    "The die rolls on screen and the number to beat is written beside it. When a blow kills you, you know which one and by how much, and you can have it replayed exactly, to show someone.",
  "Le grand livre": "The ledger",
  "Le journal": "The log",
  "Le lien a été retiré par celui ou celle qui l'avait partagé, ou il n'a jamais existé.":
    "The link was withdrawn by whoever shared it, or it never existed.",
  "Le lieu": "The place",
  "Le lieu, vu à la plume": "The place, drawn in ink",
  "Le livre dont on ne sort qu'une fois": "The book you only walk out of once",
  "Le livre n'a pas ce feuillet. Revenez à la table.": "The book has no such leaf. Come back to the table.",
  "Le mode mains libres tourne les feuillets tout seul, et s'arrête devant les choix.":
    "Hands-free turns the leaves on its own, and stops in front of the choices.",
  "Le pacte": "The pact",
  "Le pont de corde tient encore, mais les planches manquent par endroits, et le vent qui monte de la gorge sent la pierre mouillée.":
    "The rope bridge is still holding, but planks are missing here and there, and the wind rising from the gorge smells of wet stone.",
  "Le pupitre": "The desk",
  "Le rebouteux": "The bonesetter",
  "Le registre de la semaine": "This week's register",
  "Le sac": "The bag",
  "Le sac de {heros}": "{heros}'s bag",
  "Le sac et le corps restent ouverts : c'est le moment de boire, de s'équiper. Fouiller, dormir et partir attendront.":
    "The bag and the body stay open: this is the moment to drink, to gear up. Searching, sleeping and leaving will wait.",
  "Lente": "Slow",
  "Les armes sortent. L'ordre est tiré : {ordre}.": "Weapons come out. The order is drawn: {ordre}.",
  "Les aventures": "The adventures",
  "Les aventures sans plafond mensuel — trois par jour, le temps qu'elles s'écrivent.":
    "Adventures with no monthly ceiling, three a day, as fast as they can be written.",
  "Les formules sont ici": "The plans are here",
  "Les paquets sont ici": "The bundles are here",
  "Lien du carnet": "Journal link",
  "Lignes de coffre :": "Vault lines:",
  "Lire": "Read",
  "Lire plus lentement": "Read more slowly",
  "Lire plus vite": "Read faster",
  "Lire à voix haute": "Read aloud",
  "Là où vous en étiez": "Where you left off",
  "Mains libres": "Hands-free",
  "Masquer le calque": "Hide the overlay",
  "Mettre en fabrication": "Send to the workshop",
  "Mode Chronique :": "Chronicle mode:",
  "Montrer le calque": "Show the overlay",
  "Mot de passe": "Password",
  "N": "N",
  "Ne plus partager": "Stop sharing",
  "Noir : jamais vu. Pointillé : aperçu de loin — on n'en connaît que le repère. Dessiné : visité, avec son nom et son coût. Un toit marque un sanctuaire. On voyage par les chemins du texte, pas en cliquant sur la carte — un clic sur une case l'annote.":
    "Black: never seen. Dotted: glimpsed from afar, only its landmark is known. Drawn: visited, with its name and its cost. A roof marks a sanctuary. You travel by the roads in the text, not by clicking the map; a click on a tile annotates it.",
  "Normal": "Normal",
  "Normale": "Normal",
  "Nouveau héros": "New hero",
  "Nuit": "Night",
  "Nœuds": "Nodes",
  "On dépose la commande…": "Placing the order…",
  "On en gagne en jouant — vingt pour une aventure menée à son terme, cent pour un boss majeur — et l'on peut en acheter.":
    "They are earned by playing, twenty for an adventure carried to its end, a hundred for a major boss, and they can be bought.",
  "On encaisse…": "Taking payment…",
  "On grave…": "Engraving…",
  "On la déplie…": "Unfolding it…",
  "On la reconnaît à {repere}.": "You know it by {repere}.",
  "On ouvre le livre…": "Opening the book…",
  "On ouvre le registre…": "Opening the register…",
  "On revient ici en cas de reprise (mode Chronique)": "You come back here on a resume (Chronicle mode)",
  "On vous touche à": "You are hit on",
  "Or": "Gold",
  "Ouvre {quoi}": "Opens {quoi}",
  "Ouvrez un compte pour y figurer. L'aventure, elle, est gratuite.":
    "Open an account to appear on it. The adventure itself is free.",
  "Ouvrir": "Open",
  "Ouvrir le compte": "Open the account",
  "Ouvrir un compte": "Open an account",
  "Où d'autres sont tombés": "Where others fell",
  "Pacte de sous-équipement": "Underequipment pact",
  "Parchemin": "Parchment",
  "Parchemin — plein jour": "Parchment, broad daylight",
  "Parer": "Parry",
  "Partager ce carnet": "Share this journal",
  "Parti le {depart}, arrivé au bout le {arrivee}.": "Set out on {depart}, reached the end on {arrivee}.",
  "Parti le {depart}.": "Set out on {depart}.",
  "Pas assez d'Essence": "Not enough Essence",
  "Pause": "Pause",
  "Personne de vivant, pour l'instant. Il faut bien que quelqu'un y aille.":
    "Nobody alive, for now. Someone has to go.",
  "Personne n'est encore allé au bout, ni tombé en chemin. Ça ne durera pas.":
    "Nobody has reached the end yet, nor fallen on the way. That will not last.",
  "Personne n'est encore tombé. Ça ne durera pas.": "Nobody has fallen yet. That will not last.",
  "Personne ne l'avait vue": "Nobody had seen it",
  "Personne ne partira à votre place. Il vous faut un héros avant d'ouvrir une aventure — un seul à la fois, tant que le compte est gratuit.":
    "Nobody will set out in your place. You need a hero before opening an adventure, and only one at a time while the account is free.",
  "Personne à équiper.": "Nobody to equip.",
  "Plus grand": "Larger",
  "Plus lente": "Slower",
  "Plus petit": "Smaller",
  "Plus vive": "Faster",
  "Points de vie": "Hit points",
  "Porter": "Wear",
  "Porté": "Worn",
  "Portée": "Reach",
  "Posée": "Steady",
  "Prendre": "Take",
  "Pressée": "Hurried",
  "Publier": "Publish",
  "Quatre au maximum sur une même caractéristique. Et il faut les dépenser tous : il n'y a pas de « plus tard » où les garder.":
    "Four at most on any one trait. And all of them must be spent: there is no later to keep them for.",
  "Quatre héros vivants en même temps, au lieu d'un.": "Four living heroes at once instead of one.",
  "Qui a ce lien lit le carnet — le nom, la fin, la carte et le journal. Jamais le texte des nœuds : on raconte un parcours, on ne prête pas l'aventure.":
    "Whoever has this link reads the journal: the name, the ending, the map and the log. Never the text of the nodes; a run is being told, the adventure is not being lent.",
  "Qui l'on équipe": "Who is being equipped",
  "Qui part": "Who is setting out",
  "Rallumer": "Light it again",
  "Ranger": "Put away",
  "Rations": "Rations",
  "Refermer": "Close it",
  "Refermer : {blessure}": "Close it: {blessure}",
  "Refermer le livre et revenir à la table": "Close the book and go back to the table",
  "Relire le combat à voix haute": "Read the fight back aloud",
  "Renoncer": "Give up",
  "Renvoyer ce message": "Send this message again",
  "Repasser au parchemin": "Back to parchment",
  "Reprendre": "Resume",
  "Reprendre le livre": "Pick the book back up",
  "Reprendre votre voix": "Take your vote back",
  "Reprises déjà faites :": "Resumes already used:",
  "Retirer du calque": "Take off the overlay",
  "Retirer un point de {quoi}": "Remove a point of {quoi}",
  "Retirer {quoi}": "Take off {quoi}",
  "Retiré": "Retired",
  "Revenir": "Back",
  "Revenir à la table": "Back to the table",
  "Revenu au bout de {duree} de voyage.": "Came back after {duree} of travelling.",
  "Rien d'écrit pour l'instant.": "Nothing written yet.",
  "Rien de ce qui est ici ne se trouve QUE ici : tout s'obtient en jouant. Et rien n'entre dans le sac d'un héros directement — ce qu'on achète va au coffre, et l'on prépare son héros avant de partir.":
    "Nothing here is found ONLY here: everything can be had by playing. And nothing goes straight into a hero's bag; what you buy goes to the vault, and a hero is made ready before setting out.",
  "Rien en suspens. Une porte qu'on ne peut pas ouvrir, une bête qu'on a fuie, un coffre qu'on laisse : cliquez la case, et notez-le.":
    "Nothing pending. A door you cannot open, a beast you fled, a chest you left behind: click the tile and write it down.",
  "Rien encore.": "Nothing yet.",
  "Rien n'a été écrit.": "Nothing has been written.",
  "Rien ne vous a été retiré": "Nothing has been taken from you",
  "Rien.": "Nothing.",
  "Rouvrir": "Reopen",
  "Réduire le texte": "Smaller text",
  "Régler pour de faux": "Pay for pretend",
  "Réglé": "Settled",
  "Répondre": "Answer",
  "S'abonner": "Subscribe",
  "Sa fiche": "His sheet",
  "Sac trop lourd : l'Agilité en pâtit": "Bag too heavy: Agility suffers for it",
  "Sanctuaire : on y dort, on s'y fait soigner.": "Sanctuary: you sleep here, you are healed here.",
  "Sceau de la fin": "Seal of the ending",
  "Sceau de la semaine": "Seal of the week",
  "Sceau du cimetière": "Seal of the graveyard",
  "Sceller ce héros": "Seal this hero",
  "Se cacher": "Hide",
  "Se défaire de ce contenant — vous perdrez les emplacements qu'il porte, et il est perdu":
    "Get rid of this container: you lose the slots it carries, and it is gone",
  "Se défaire de cet objet — il ne reste pas au sol, il est perdu":
    "Get rid of this item: it does not stay on the ground, it is gone",
  "Se garder": "Guard",
  "Serré": "Tight",
  "Six façons de se tirer d'un mauvais pas. Aucune n'est meilleure : elles perdent seulement à des endroits différents.":
    "Six ways out of a bad spot. None is better than the others: they simply lose in different places.",
  "Son journal": "His log",
  "Son nom": "His name",
  "Suite": "Next",
  "Suivre": "Follow",
  "Sur {ou}": "On {ou}",
  "Sur lui": "On him",
  "Surchargé : l'Agilité en souffre, et au-delà d'une moitié de plus, on ne bouge plus.":
    "Overloaded: Agility suffers, and past half again you stop moving altogether.",
  "Suspendre la lecture": "Pause the reading",
  "Tant que ce n'est pas soigné.": "Until it is treated.",
  "Titre (facultatif)": "Title (optional)",
  "Tombé": "Fell",
  "Tombé après {duree} de voyage, {cause}.": "Fell after {duree} of travelling, {cause}.",
  "Tombé après {duree} de voyage.": "Fell after {duree} of travelling.",
  "Tombé le jour": "Fell on day",
  "Tombé — {ou}": "Fell at {ou}",
  "Tombés récemment": "Recently fallen",
  "Ton (facultatif)": "Tone (optional)",
  "Toucher": "To hit",
  "Tour {numero}": "Turn {numero}",
  "Tour {tour}": "Turn {tour}",
  "Tout ce que le Chroniqueur a, sans les comptes.": "Everything the Chronicler has, without the counting.",
  "Tout dérouler": "Unroll it all",
  "Tout revoir": "See it all again",
  "Très grand": "Very large",
  "Un adversaire vous barre le passage": "One enemy blocks your way",
  "Un autre héros": "Another hero",
  "Un carnet de Destinia — le livre dont on ne sort qu'une fois.":
    "A journal from Destinia, the book you only walk out of once.",
  "Un compte gratuit tient un héros vivant à la fois. C'est assez pour mourir plusieurs fois.":
    "A free account holds one living hero at a time. That is enough to die several times.",
  "Un coup qui porte peut infliger {etat} — {effet}": "A blow that lands can inflict {etat}: {effet}",
  "Un héros vivant à la fois, une aventure écrite pour vous par mois, vingt nœuds écoutés par jour. C'est le jeu entier — la mort y est définitive, et c'est ainsi qu'il a été écrit.":
    "One living hero at a time, one adventure written for you each month, twenty nodes listened to a day. That is the whole game: death is final in it, and that is how it was written.",
  "Un instant…": "One moment…",
  "Un monde que personne n'a lu": "A world nobody has read",
  "Un nouveau héros": "A new hero",
  "Un raté ne vous coûte que du temps": "A failure costs you only time",
  "Un raté {suite}": "A failure {suite}",
  "Une autre aventure": "Another adventure",
  "Une aventure ne s'est pas écrite jusqu'au bout. Elle ne vous a rien coûté.":
    "An adventure did not write itself all the way through. It cost you nothing.",
  "Une fois scellé, rien de tout cela ne se modifie. Jamais.": "Once sealed, none of this changes. Ever.",
  "Une ligne par sorte d'objet, quelle qu'en soit la quantité.":
    "One line per kind of item, whatever the quantity.",
  "Une seule vie": "One life only",
  "Veillée": "Vigil",
  "Veillée — lampe éteinte": "Vigil, lamp out",
  "Victoire, en {tours} tours.": "Victory, in {tours} turns.",
  "Viser {qui}": "Target {qui}",
  "Vivant": "Alive",
  "Vive": "Brisk",
  "Voir le registre": "See the register",
  "Vos héros": "Your heroes",
  "Vos héros vous attendent là où vous les avez laissés.": "Your heroes are waiting where you left them.",
  "Votre abonnement a expiré le {date}.": "Your subscription expired on {date}.",
  "Votre abonnement a expiré.": "Your subscription has expired.",
  "Votre aventure est prête. Elle vous attend au catalogue.":
    "Your adventure is ready. It is waiting in the catalogue.",
  "Votre coffre : {n} ligne sur {total}.": "Your vault: {n} line of {total}.",
  "Votre coffre : {n} lignes sur {total}.": "Your vault: {n} lines of {total}.",
  "Votre héros ne deviendra pas plus fort en chemin : il n'aura que ce que vous lui donnerez au départ, et ce qu'il ramassera. S'il meurt, il ne revient pas — son nom passe au cimetière, où n'importe qui peut venir le lire.":
    "Your hero will not grow stronger along the way: he will have what you give him at the start, and what he picks up. If he dies, he does not come back; his name goes to the graveyard, where anyone can come and read it.",
  "Votre parcours": "Your run",
  "Votre réponse à l'énigme": "Your answer to the riddle",
  "Votre tour vient": "Your turn is coming",
  "Vous": "You",
  "Vous avez fouillé les lieux.": "You have searched the place.",
  "Vous disparaissez de la vue.": "You slip out of sight.",
  "Vous encaissez": "You take",
  "Vous esquivez {geste} de {acteur} — d20 {d20}, total {total}.":
    "You dodge {geste} from {acteur}: d20 {d20}, total {total}.",
  "Vous lancez {sort} sur {cible} — {cout} d'Essence.": "You cast {sort} at {cible}: {cout} Essence.",
  "Vous manquez tout — un 1 naturel, et vous perdez votre réaction jusqu'au tour suivant.":
    "You miss everything: a natural 1, and you lose your reaction until the next turn.",
  "Vous manquez {cible}.": "You miss {cible}.",
  "Vous ne trouvez pas d'ombre où vous mettre.": "You find no shadow to slip into.",
  "Vous ouvrez un livre que personne n'a lu avant vous. Vous avancez de page en page, vous notez sur la carte ce que vous avez vu, et vous décidez seul — sans reprise, sans partie sauvegardée juste avant, sans seconde chance.":
    "You open a book nobody has read before you. You move page by page, you write down on the map what you have seen, and you decide alone: no retries, no save from just before, no second chance.",
  "Vous parez : +{bonus} en Défense jusqu'au tour suivant.": "You parry: +{bonus} Defence until the next turn.",
  "Vous perdez votre tour — {etat}.": "You lose your turn: {etat}.",
  "Vous rompez le combat après {tours} tours.": "You break off after {tours} turns.",
  "Vous rompez le combat et partez.": "You break off and walk away.",
  "Vous tentez de fuir, sans y parvenir.": "You try to flee, and fail.",
  "Vous tombez.": "You fall.",
  "Vous touchez {cible} : {degats} dégâts.": "You hit {cible}: {degats} damage.",
  "Vous usez d'un objet : {detail}.": "You use an item: {detail}.",
  "Vous verrez le dé": "You will see the die",
  "Vous vous mettez en garde : +{bonus} à l'esquive jusqu'au tour suivant.":
    "You take a guard: +{bonus} to dodging until the next turn.",
  "Vous êtes entré dans cette aventure en dessous de son seuil de puissance : elle ne s'est pas adoucie pour autant.":
    "You walked into this adventure below its power floor: it did not soften for that.",
  "Vous êtes ici — {lieu}": "You are here: {lieu}",
  "Vous êtes inscrit au registre — votre parcours y entre comme celui des autres. La liste, elle, se lit avec un abonnement.":
    "You are on the register: your run goes in like everybody else's. Reading the list, though, needs a subscription.",
  "Vous êtes {etat} — {effet} {duree}": "You are {etat}: {effet} {duree}",
  "Vous êtes {palier}": "You are {palier}",
  "Vu": "Seen",
  "après {duree}": "after {duree}",
  "aucune porte n'attend une clef qu'on aurait oublié de poser":
    "no door waits on a key nobody remembered to place",
  "base": "base",
  "cent vingt signes, pas un de plus": "one hundred and twenty characters, not one more",
  "d20 + Attaque contre sa défense": "d20 + Attack against its defence",
  "d20 + {mod}": "d20 + {mod}",
  "dix caractères au moins": "ten characters at least",
  "donne le cap sur la carte": "gives a bearing on the map",
  "défense renforcée jusqu'à votre prochain tour — vous ne frappez pas":
    "defence raised until your next turn; you do not strike",
  "dégâts dans cet échange.": "damage in this exchange.",
  "déjà entaillée {combien}×": "already cut {combien}×",
  "en chemin": "on the way",
  "esquive renforcée, et l'esquive reste gardée — vous ne frappez pas":
    "dodging raised, and the dodge stays held; you do not strike",
  "est celle de cette semaine : la même pour tout le monde, gratuite, hors quota — et la seule où l'on puisse se comparer.":
    "is this week's: the same for everybody, free, outside the quota, and the only one where runs can be compared.",
  "est en route. On prépare son héros avant de partir, on assume ensuite — le coffre se rouvrira à son retour, ou à sa mort.":
    "is on the road. A hero is made ready before setting out, and lived with afterwards; the vault reopens on his return, or on his death.",
  "fragments": "shards",
  "fragments — on en gagne vingt par aventure terminée, cent par boss abattu":
    "shards, twenty for each adventure finished, a hundred for each boss brought down",
  "il vous faut {n} ration": "you need {n} ration",
  "il vous faut {n} rations": "you need {n} rations",
  "il vous faut {quoi}": "you need {quoi}",
  "jet d'Agilité contre leur vigilance — caché, on frappe mieux":
    "an Agility roll against their watchfulness; hidden, you strike better",
  "kit de campement requis": "camp kit required",
  "l'adversaire": "the enemy",
  "la vôtre": "yours",
  "mains libres": "hands-free",
  "malus au toucher, dégâts doublés si le coup porte":
    "a penalty to hit, damage doubled if the blow lands",
  "on ne revient pas dans ce combat, et le butin reste ici":
    "there is no coming back into this fight, and the loot stays here",
  "ouvert": "open",
  "ouvre {quoi}": "opens {quoi}",
  "pour un héros armé": "for a hero armed at",
  "referme {blessure}": "closes {blessure}",
  "réservé aux abonnés": "subscribers only",
  "sans coût d'Essence — la capacité de votre archétype":
    "no Essence cost; your archetype's knack",
  "sans limite": "no limit",
  "sans plafond ({parJour} / jour)": "no ceiling ({parJour} / day)",
  "semaine {semaine} de {annee}": "week {semaine} of {annee}",
  "sombre, ironique, épique…": "dark, wry, epic…",
  "sur 3.": "of 3.",
  "sur place": "on the spot",
  "sur une caractéristique de votre choix. Trois au maximum, et ensuite ce mode se ferme.":
    "on a trait of your choosing. Three at most, and then this mode closes.",
  "sur {n} passage.": "out of {n} passage.",
  "sur {n} passages.": "out of {n} passages.",
  "un autre héros": "another hero",
  "un mot": "one word",
  "une attaque": "an attack",
  "une attaque puissante": "a heavy attack",
  "une clef": "a key",
  "une fois de plus": "one more time",
  "vers {lieu}": "towards {lieu}",
  "vers {lieu} — on n'en revient pas de sitôt": "towards {lieu}; there is no coming back soon",
  "vous": "you",
  "vous blesse": "wounds you",
  "vous jette plus loin": "throws you further off",
  "vous jette vers {lieu}": "throws you towards {lieu}",
  "vous@exemple.fr": "you@example.com",
  "{acteur} disparaît de la vue.": "{acteur} slips out of sight.",
  "{acteur} lance {sort} sur {cible} — {cout} d'Essence.": "{acteur} casts {sort} at {cible}: {cout} Essence.",
  "{acteur} manque tout — un 1 naturel, et il perd sa réaction jusqu'au tour suivant.":
    "{acteur} misses everything: a natural 1, and loses its reaction until the next turn.",
  "{acteur} ne trouve pas d'ombre où se mettre.": "{acteur} finds no shadow to slip into.",
  "{acteur} pare : +{bonus} en Défense jusqu'au tour suivant.":
    "{acteur} parries: +{bonus} Defence until the next turn.",
  "{acteur} perd son tour — {etat}.": "{acteur} loses its turn: {etat}.",
  "{acteur} rate {cible}.": "{acteur} misses {cible}.",
  "{acteur} rompt le combat et part.": "{acteur} breaks off and leaves.",
  "{acteur} se met en garde : +{bonus} à l'esquive jusqu'au tour suivant.":
    "{acteur} takes a guard: +{bonus} to dodging until the next turn.",
  "{acteur} tente de fuir, sans y parvenir.": "{acteur} tries to flee, and fails.",
  "{acteur} touche {cible} : {degats} dégâts.": "{acteur} hits {cible}: {degats} damage.",
  "{acteur} use d'un objet : {detail}.": "{acteur} uses an item: {detail}.",
  "{acteur} vous rate.": "{acteur} misses you.",
  "{acteur} vous touche : {degats} dégâts.": "{acteur} hits you: {degats} damage.",
  "{acteur} — {capacite} : {detail}.": "{acteur}, {capacite}: {detail}.",
  "{archetype} — mort : {cause}": "{archetype}, dead: {cause}",
  "{archetype} — mort en chemin": "{archetype}, dead on the way",
  "{cible} esquive {geste} — d20 {d20}, total {total}.": "{cible} dodges {geste}: d20 {d20}, total {total}.",
  "{cible} est {etat} — {effet} {duree}": "{cible} is {etat}: {effet} {duree}",
  "{cible} s'effondre.": "{cible} goes down.",
  "{cout} d'Essence — la capacité de votre archétype": "{cout} Essence; your archetype's knack",
  "{cout} d'Essence — sans dégât": "{cout} Essence; no damage",
  "{cout} d'Essence — {degats} + Essence, ignore l'armure":
    "{cout} Essence; {degats} + Essence, ignores armour",
  "{debut} et {fin}": "{debut} and {fin}",
  "{duree} de jeu": "{duree} of play",
  "{duree} de jeu — jour {jour}": "{duree} of play, day {jour}",
  "{effet} — et cela coûte le tour": "{effet}, and it costs the turn",
  "{essence} d'Essence sur {max}": "{essence} Essence out of {max}",
  "{etat} : {n} dégât sur {cible}.": "{etat}: {n} damage on {cible}.",
  "{etat} : {n} dégâts sur {cible}.": "{etat}: {n} damage on {cible}.",
  "{etat} se dissipe sur {cible}.": "{etat} fades on {cible}.",
  "{heros} a déjà abattu ce qui garde ce lieu, en {n} tour.":
    "{heros} has already brought down what guards this place, in {n} turn.",
  "{heros} a déjà abattu ce qui garde ce lieu, en {n} tours.":
    "{heros} has already brought down what guards this place, in {n} turns.",
  "{heros} a déjà abattu ce qui garde ce lieu.": "{heros} has already brought down what guards this place.",
  "{heros} a fouillé les lieux avant vous.": "{heros} searched this place before you.",
  "{heros} a laissé un mot ici.": "{heros} left a note here.",
  "{heros} a péri ici — {cause}.": "{heros} died here: {cause}.",
  "{heros} a péri ici.": "{heros} died here.",
  "{heros} en est sorti": "{heros} walked out",
  "{heros} est tombé": "{heros} has fallen",
  "{heures} h": "{heures} h",
  "{heures} h de lumière": "{heures} h of light",
  "{lieu} — {biome}, traversée {duree}": "{lieu}: {biome}, crossing {duree}",
  "{lieu}, vue à la plume": "{lieu}, drawn in ink",
  "{nom} — {archetype} (équipement {ipe})": "{nom}, {archetype} (gear {ipe})",
  "{nom}, dessinée à la plume": "{nom}, drawn in ink",
  "{n} acte": "{n} act",
  "{n} actes": "{n} acts",
  "{n} adversaires vous barrent le passage": "{n} enemies block your way",
  "{n} cicatrice en chemin.": "{n} scar on the way.",
  "{n} cicatrices en chemin.": "{n} scars on the way.",
  "{n} héros tombé ici": "{n} hero fell here",
  "{n} héros tombés ici": "{n} heroes fell here",
  "{n} nœud": "{n} node",
  "{n} nœuds": "{n} nodes",
  "{n} objet sur le corps.": "{n} item on the body.",
  "{n} objets sur le corps.": "{n} items on the body.",
  "{n} pièce ramassée.": "{n} coin picked up.",
  "{n} pièces ramassées.": "{n} coins picked up.",
  "{n} reprise": "{n} resume",
  "{n} reprises": "{n} resumes",
  "{n} tour.": "{n} turn.",
  "{n} tours.": "{n} turns.",
  "{objet} — au coffre.": "{objet}, into the vault.",
  "{or} d'or": "{or} gold",
  "{poids} / {capacite} kg — {pris} / {total} emplacements.":
    "{poids} / {capacite} kg, {pris} / {total} slots.",
  "{pourcent} % en sont repartis vivants,": "{pourcent} % walked away alive,",
  "{pv} points de vie sur {max}": "{pv} hit points out of {max}",
  "· cicatrice": "· scar",
  "· cicatrices portées :": "· scars carried:",
  "À court de fragments ?": "Out of shards?",
  "À l'atelier": "In the workshop",
  "À répartir": "To spend",
  "À terre": "Down",
  "À tâtons : {malus} à tous les jets, une heure de plus pour avancer, et l'on ne trouve rien en fouillant":
    "Groping: {malus} to every roll, an extra hour to move, and searching turns up nothing",
  "Échoppe": "Shop",
  "Éclaire {heures} heures de marche": "Lights {heures} hours of walking",
  "Éclairé": "Lit",
  "Écrire sur la carte": "Write on the map",
  "Écrire sur {lieu}": "Write on {lieu}",
  "Équipé": "Equipped",
  "Éteindre": "Put it out",
  "Éteindre la lampe": "Put the lamp out",
  "Étouffer": "Smother it",
  "×": "×",
  "équipement": "gear",
  "— comptez une demi-heure. Rien à surveiller : fermez l'onglet, allez faire autre chose, on vous préviendra ici.":
    ": count on half an hour. Nothing to watch: close the tab, go and do something else, you will be told here.",
  "— et rien ne vous empêche de partir en permadeath, qui est le jeu tel qu'il a été écrit.":
    ", and nothing stops you setting out in permadeath, which is the game as it was written.",
  "— la même pour tout le monde, tirée d'une graine commune. C'est le seul endroit où deux joueurs traversent exactement le même monde.":
    ", the same for everybody, drawn from one shared seed. It is the only place where two players cross exactly the same world.",
  "— la voix tourne les feuillets et s'arrête devant les choix":
    ": the voice turns the leaves and stops in front of the choices",
  "— les autres joueurs de cette aventure la verront, signée de votre héros":
    ": the other players of this adventure will see it, signed by your hero",
  "— on s'en assure avant d'en écrire la première phrase.":
    ": that is made certain before its first sentence is written.",
  "— se dépense au départ d'une aventure": ": spent at the start of an adventure",
  "— tour {tour}": ", turn {tour}",
  "— vous frappez ET vous gardez une esquive, une seule, jouée après le coup adverse":
    ": you strike AND you keep one dodge, a single one, played after the other side's blow",
  "−1 définitif": "a permanent −1",
  "−{malus} au toucher, dégâts doublés si le coup porte":
    "−{malus} to hit, damage doubled if the blow lands",

  /* --- Le cartouche de la carte (§4.2) -------------------------------------- */

  "Passez la main sur une case : ce qui y est écrit s'affichera ici.":
    "Run your hand over a tile: whatever is written on it shows up here.",
  "Rien d'écrit ici.": "Nothing written here.",
  "case choisie": "tile held",
  "une autre main": "another hand",

  /* --- Les échéances de l'horloge (§3.4) ------------------------------------ */

  /* Le titre et la conséquence sont des CITATIONS de l'aventure (§13.1) : seuls les deux-points
     entre eux appartiennent à l'interface, et c'est tout ce qui se traduit ici. */
  "{titre} : {consequence}": "{titre}: {consequence}",
  "Échéance": "Deadline",
  "{titre} — ce qui vient, et ce qui ne se rattrape pas": "{titre}: what is coming, and what cannot be caught up",
  "{titre}. Les échéances déjà tombées coûtent {heures} h par déplacement.":
    "{titre}. The deadlines already past cost {heures} h per move.",

  /* --- Le carnet de quêtes (§4.6) ------------------------------------------- */

  /* Les titres et résumés des quêtes sont des CITATIONS de l'aventure (§13.1) : seul le cadre
     autour d'eux se traduit. */
  "Ce que vous poursuivez": "What you are after",
  "Ce qui s'est passé": "What happened",
  "Il reste {n} étape après celle-là.": "{n} step left after this one.",
  "Il reste {n} étapes après celle-là.": "{n} steps left after this one.",
  "Menée à son terme.": "Carried through.",

  /* --- Le hors-ligne (§14) -------------------------------------------------- */

  "Hors ligne.": "Offline.",
  "Le serveur n'est pas joignable. Rien n'a été joué : ce qui est à l'écran attend que la connexion revienne.":
    "The server cannot be reached. Nothing was played: what is on screen is waiting for the connection to come back.",
  "Rien n'a été joué : ce qui est à l'écran vous reste, et les gestes qui écrivent attendent le retour de la connexion.":
    "Nothing was played: what is on screen stays yours, and anything that writes is waiting for the connection to come back."
};
